import { componentCls } from "@faire/slate/lib/tracking";
import { AccordionChevron } from "@faire/slate/private/accordion/AccordionChevron";
import { AccordionItemBase } from "@faire/slate/private/accordion/AccordionItemBase";
import { AccordionItemContent } from "@faire/slate/private/accordion/AccordionItemContent";
import { AccordionItemLabel } from "@faire/slate/private/accordion/AccordionItemLabel";
import { AccordionItemSummary } from "@faire/slate/private/accordion/AccordionItemSummary";
import {
  AccordionLabelVariant,
  IAccordionItemProps,
} from "@faire/slate/private/accordion/types";
import type { IText } from "@faire/slate/private/shared/types";
import React from "react";

type IProps = {
  /**
   * Controls the open state of the accordion item.
   */
  expanded?: boolean;

  /**
   * The label of the accordion item.
   */
  label: IText;

  /**
   * The typography variant of the accordion item label.
   *
   * @default "subheading"
   */
  labelVariant?: AccordionLabelVariant;

  /**
   * The content of the accordion item. When content is a string, it will be
   * rendered as a paragraph, otherwise it will be rendered as-is.
   */
  content: string | React.ReactNode;

  /**
   * **web**
   * Event handler for the toggle event.
   * @ref https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/toggle_event
   */
  onToggle?: React.ToggleEventHandler<HTMLDetailsElement>;

  // DO NOT ADD any new prop without aligning with Slate team first
} & IAccordionItemProps;

/**
 * An accordion item is a single section of an accordion group.
 *
 * See [migration guide](migration-guide.mdx) for more information.
 *
 * {@link https://www.notion.so/faire/Accordion-2242efb5c25a80d68921e5c3b806f984 | Slate/AccordionItem}
 */
export const AccordionItem: React.FC<IProps> = ({
  content,
  expanded,
  label,
  labelVariant = "subheading",
  ...props
}) => {
  return (
    <AccordionItemBase
      {...props}
      className={componentCls()}
      data-component-name="Slate/AccordionItem"
      open={expanded}
    >
      <AccordionItemSummary>
        <AccordionItemLabel variant={labelVariant}>{label}</AccordionItemLabel>
        <AccordionChevron />
      </AccordionItemSummary>
      <AccordionItemContent>
        {typeof content === "string" ? <p>{content}</p> : content}
      </AccordionItemContent>
    </AccordionItemBase>
  );
};
